import {
  Alert,
  Anchor,
  Button,
  Card,
  Code,
  Group,
  List,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { semanticColor } from "@how/design-system";
import { ConfirmActionModal, ErrorState, LoadingState, PageHeader, StatusBadge } from "@how/ui";
import { RecordList } from "../domains/DnsRecords";
import {
  checkedAt,
  DOMAIN_STATUS_LABELS,
  domainErrorMessage,
  hostnameProblem,
  isSettling,
  MAX_DOMAINS,
  tidyHostname,
  useDomainSetup,
  useRecheckDomain,
  useRemoveDomain,
  useRequestDomain,
  type CustomDomain,
} from "./domain-queries";
import { WebsiteTabs } from "./WebsiteTabs";
import { DomainSteps } from "./studio/DomainSteps";
import { StudioShell } from "./studio/StudioShell";
import { useWebsiteVariant } from "./website-variant";

/** What is happening now, in a sentence, under the badge. */
function progressLine(domain: CustomDomain): string {
  switch (domain.status) {
    case "pending_verification":
      return "HoW is looking for your DNS record. It checks every minute, and this page updates on its own. A new record usually shows within minutes; some registrars take up to a day.";
    case "verifying":
      return "Your record is in place. HoW is issuing the security certificate, which usually takes a few minutes.";
    case "active":
      return domain.isPrimary
        ? "Your website answers at this address, and it is the one your links and search results use. The security certificate was issued and renews automatically."
        : "Your website answers at this address. The security certificate was issued and renews automatically.";
    case "failed":
      return "HoW could not connect this address. Check the record below, then choose Check now.";
    case "disabled":
      return "DCX Logic turned this address off. Contact support@dcxlogic.com.";
  }
}

function DomainCard({
  domain,
  headingOrder = 2,
}: {
  readonly domain: CustomDomain;
  readonly headingOrder?: 2 | 3;
}) {
  const recheck = useRecheckDomain();
  const remove = useRemoveDomain();
  const [confirming, setConfirming] = useState(false);
  const routing = domain.records.filter((r) => r.purpose === "routing");
  const extra = domain.records.filter((r) => r.purpose !== "routing");
  const live = domain.status === "active";

  return (
    <Card data-domain={domain.hostname}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" gap="sm">
          <Stack gap={4} style={{ minWidth: 0 }}>
            <Title order={headingOrder} size="h4" style={{ overflowWrap: "anywhere" }}>
              {domain.hostname}
            </Title>
            <Group gap="xs">
              <StatusBadge
                domain="domain_status"
                value={domain.status}
                label={DOMAIN_STATUS_LABELS[domain.status]}
              />
              {domain.lastCheckedAt !== null && !live ? (
                <Text size="xs" c="dimmed">
                  Last checked {checkedAt(domain.lastCheckedAt)}
                </Text>
              ) : null}
            </Group>
          </Stack>
          <Group gap="xs">
            {live ? (
              <Button
                component="a"
                href={`https://${domain.hostname}`}
                target="_blank"
                rel="noreferrer"
                variant="default"
                size="compact-sm"
              >
                Open
              </Button>
            ) : domain.status === "disabled" ? null : (
              <Button
                variant="default"
                size="compact-sm"
                loading={recheck.isPending}
                onClick={() => {
                  recheck.mutate(domain.id, {
                    onSuccess: () => {
                      notifications.show({
                        color: semanticColor("info"),
                        title: "Checking now",
                        message: `HoW is looking at ${domain.hostname} again. This page updates on its own.`,
                      });
                    },
                    onError: (error) => {
                      notifications.show({
                        color: semanticColor("danger"),
                        title: "Could not check",
                        message: domainErrorMessage(error),
                      });
                    },
                  });
                }}
              >
                Check now
              </Button>
            )}
            <Button
              variant="subtle"
              color={semanticColor("danger")}
              size="compact-sm"
              onClick={() => {
                setConfirming(true);
              }}
            >
              Remove
            </Button>
          </Group>
        </Group>

        <Text size="sm" role="status">
          {progressLine(domain)}
        </Text>
        {domain.lastError !== null && !live ? (
          <Alert
            color={semanticColor(domain.status === "failed" ? "danger" : "info")}
            title={domain.status === "failed" ? "What went wrong" : "What HoW last saw"}
          >
            {domain.lastError}
          </Alert>
        ) : null}

        {live || domain.status === "disabled" ? null : (
          <Stack gap="xs">
            <Text size="sm" fw={600}>
              1. Add this record where you bought {domain.rootDomain}
            </Text>
            <Text size="sm" c="dimmed">
              Sign in to the company you bought the domain from (GoDaddy, Namecheap, Squarespace,
              Cloudflare and so on), open its DNS settings and add one record. If a record named{" "}
              {routing[0]?.name ?? "www"} is already there, change it to this.
            </Text>
            <RecordList records={routing} caption="The record to add" />
            {extra.length > 0 ? (
              <>
                <Text size="sm" c="dimmed">
                  Optional. If {domain.hostname} already has a website and you want no gap while it
                  moves, add this record first, wait for this page to say the certificate is issued,
                  and then add the record above.
                </Text>
                <RecordList records={extra} caption="Optional record, for moving a live site" />
              </>
            ) : null}
            <Text size="sm" fw={600}>
              2. Wait for Live
            </Text>
            <Text size="sm" c="dimmed">
              You can close this page. HoW keeps checking and issues the security certificate for
              you.
            </Text>
          </Stack>
        )}

        <Stack gap={4}>
          <Text size="sm" fw={600}>
            {live ? "The address without www" : "3. Forward the address without www"}
          </Text>
          <Text size="sm" c="dimmed">
            So that {domain.rootDomain} works too, turn on domain forwarding (some companies call it
            URL redirect) where you bought the domain: forward <Code>{domain.rootDomain}</Code> to{" "}
            <Code>{`https://${domain.hostname}`}</Code> and choose permanent (301). HoW cannot do
            this part for you, because it is a setting of your domain, not of your website.
          </Text>
        </Stack>
      </Stack>

      <ConfirmActionModal
        opened={confirming}
        onClose={() => {
          setConfirming(false);
        }}
        title={`Remove ${domain.hostname}?`}
        message={
          live
            ? `Your website will stop answering at ${domain.hostname} within a few minutes. It stays available at its HoW address, and you can connect the domain again later.`
            : `HoW will stop looking for ${domain.hostname}. You can connect it again later.`
        }
        confirmLabel="Remove"
        danger
        loading={remove.isPending}
        onConfirm={() => {
          remove.mutate(domain.id, {
            onSuccess: () => {
              setConfirming(false);
              notifications.show({
                color: semanticColor("success"),
                title: "Domain removed",
                message: `${domain.hostname} is no longer connected.`,
              });
            },
            onError: (error) => {
              setConfirming(false);
              notifications.show({
                color: semanticColor("danger"),
                title: "Domain not removed",
                message: domainErrorMessage(error),
              });
            },
          });
        }}
      />
    </Card>
  );
}

function ConnectForm({
  first,
  target,
  headingOrder = 2,
}: {
  readonly first: boolean;
  readonly target: string;
  readonly headingOrder?: 2 | 3;
}) {
  const request = useRequestDomain();
  const [value, setValue] = useState("");
  const [serverError, setServerError] = useState<string | null>(null);
  const problem = hostnameProblem(value);
  const host = tidyHostname(value);

  return (
    <Card>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (host === "" || problem !== null) return;
          setServerError(null);
          request.mutate(host, {
            onSuccess: () => {
              setValue("");
              notifications.show({
                color: semanticColor("success"),
                title: "Domain added",
                message: `Now add the record shown for ${host}.`,
              });
            },
            onError: (error) => {
              setServerError(domainErrorMessage(error));
            },
          });
        }}
      >
        <Stack gap="sm">
          <div>
            <Title order={headingOrder} size="h4">
              {first ? "Use your church's own domain" : "Connect another address"}
            </Title>
            <Text size="sm" c="dimmed">
              {first
                ? `Put your website on an address your church owns, such as www.yourchurch.org. You add one record where you bought the domain (it points to ${target}); HoW does the rest, including the security certificate. It is part of every plan.`
                : "For example a second domain your church owns. Each one gets its own record."}
            </Text>
          </div>
          <TextInput
            label="Your church's address"
            description="Enter it with www."
            placeholder="www.yourchurch.org"
            value={value}
            onChange={(event) => {
              setValue(event.currentTarget.value);
              setServerError(null);
            }}
            error={serverError ?? problem}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            inputMode="url"
            maw={480}
          />
          <Group>
            <Button
              type="submit"
              loading={request.isPending}
              disabled={host === "" || problem !== null}
            >
              Connect
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}

/**
 * Website → Domain (ADR 0051): the church types its address, adds one CNAME where it bought the domain, and watches
 * the address go from waiting to live. HoW creates the hostname at Cloudflare, issues the certificate and routes the
 * visitors; the root domain forwards to www at the registrar, and the page says how. Owner only (`settings.manage`).
 */
export function DomainPage() {
  const studio = useWebsiteVariant() === "studio";
  const setup = useDomainSetup();
  if (studio) return <DomainStudio setup={setup} />;
  const header = (
    <>
      <PageHeader title="Website" description="Your website on your church's own address." />
      <WebsiteTabs active="domain" />
    </>
  );
  if (setup.isPending) {
    return (
      <>
        {header}
        <LoadingState rows={3} label="Loading your domain" />
      </>
    );
  }
  if (setup.isError) {
    return (
      <>
        {header}
        <ErrorState
          message="Could not load your domain."
          onRetry={() => {
            void setup.refetch();
          }}
        />
      </>
    );
  }
  const { domains, platformHost, target } = setup.data;
  const settling = domains.some(isSettling);
  return (
    <>
      {header}
      <Stack gap="md">
        {platformHost === null ? null : (
          <Card>
            <Stack gap={4}>
              <Title order={2} size="h4">
                Your HoW address
              </Title>
              <Text size="sm">
                <Anchor href={`https://${platformHost}`} target="_blank" rel="noreferrer">
                  {platformHost}
                </Anchor>
              </Text>
              <Text size="sm" c="dimmed">
                Your website always answers here, with or without a domain of your own.
              </Text>
            </Stack>
          </Card>
        )}
        {domains.map((d) => (
          <DomainCard key={d.id} domain={d} />
        ))}
        {/* Announced once for the whole page, so a screen reader hears that it refreshes by itself. */}
        {settling ? (
          <Text size="xs" c="dimmed" aria-live="polite">
            This page refreshes every few seconds while an address is on its way to Live.
          </Text>
        ) : null}
        {domains.length < MAX_DOMAINS ? (
          <ConnectForm first={domains.length === 0} target={target} />
        ) : (
          <Text size="sm" c="dimmed">
            A church connects up to {MAX_DOMAINS} addresses. Remove one to add another.
          </Text>
        )}
        {domains.length === 0 ? <HowItWorks /> : null}
      </Stack>
    </>
  );
}

/** The four steps, before a church has connected anything. */
function HowItWorks({ headingOrder = 2 }: { readonly headingOrder?: 2 | 3 }) {
  return (
    <Card>
      <Stack gap="xs">
        <Title order={headingOrder} size="h4">
          How it works
        </Title>
        <List type="ordered" size="sm" spacing={6}>
          <List.Item>Enter your address above, with www.</List.Item>
          <List.Item>
            Add the one record this page shows you, where you bought your domain.
          </List.Item>
          <List.Item>
            Watch it go Live here. HoW issues the security certificate automatically.
          </List.Item>
          <List.Item>
            Forward the address without www to the www one, at the same company. This page explains
            how once you have added your address.
          </List.Item>
        </List>
      </Stack>
    </Card>
  );
}

type DomainSetupQuery = ReturnType<typeof useDomainSetup>;

/**
 * Website → Domain in the studio design: the HoW address as a browser bar, each connected address with its progress
 * from added to live over its card, and connecting another in the side column with how it works.
 */
function DomainStudio({ setup }: { readonly setup: DomainSetupQuery }) {
  const description = "Your website on your church's own address.";
  if (setup.isPending) {
    return (
      <StudioShell section="domain" title="Domain" description={description}>
        <LoadingState rows={3} label="Loading your domain" />
      </StudioShell>
    );
  }
  if (setup.isError) {
    return (
      <StudioShell section="domain" title="Domain" description={description}>
        <ErrorState
          message="Could not load your domain."
          onRetry={() => {
            void setup.refetch();
          }}
        />
      </StudioShell>
    );
  }
  const { domains, platformHost, target } = setup.data;
  const settling = domains.some(isSettling);
  return (
    <StudioShell section="domain" title="Domain" description={description}>
      <div className="ws-domains">
        <div className="ws-domains__main">
          {platformHost === null ? null : (
            <section className="ws-panel ws-address" aria-labelledby="ws-how-address">
              <h3 className="ws-panel__title" id="ws-how-address">
                Your HoW address
              </h3>
              <p className="ws-panel__text">
                Your website always answers here, with or without a domain of your own.
              </p>
              <div className="ws-address__bar">
                <span className="ws-thumb__dots" aria-hidden>
                  <i />
                  <i />
                  <i />
                </span>
                <span className="ws-address__url">
                  <span className="ws-address__lock" aria-hidden>
                    https://
                  </span>
                  {platformHost}
                </span>
                <Button
                  component="a"
                  href={`https://${platformHost}`}
                  target="_blank"
                  rel="noreferrer"
                  size="compact-sm"
                  variant="default"
                >
                  Open
                </Button>
              </div>
            </section>
          )}
          {domains.map((d) => (
            <div key={d.id} className="ws-domain">
              <DomainSteps domain={d} />
              <DomainCard domain={d} headingOrder={3} />
            </div>
          ))}
          {settling ? (
            <Text size="xs" c="dimmed" aria-live="polite">
              This page refreshes every few seconds while an address is on its way to Live.
            </Text>
          ) : null}
        </div>
        <div className="ws-domains__side">
          {domains.length < MAX_DOMAINS ? (
            <ConnectForm first={domains.length === 0} target={target} headingOrder={3} />
          ) : (
            <Text size="sm" c="dimmed">
              A church connects up to {MAX_DOMAINS} addresses. Remove one to add another.
            </Text>
          )}
          <HowItWorks headingOrder={3} />
        </div>
      </div>
    </StudioShell>
  );
}
